import api from "./api";

export interface DashboardStat {
  value: number;
  previousValue: number;
}

export interface DashboardOverview {
  activeUsersToday: DashboardStat;
  workoutsCompletedToday: DashboardStat;
  membersAtRisk: DashboardStat;
  inactivePremium7d: DashboardStat;
  trialsEnding48h: DashboardStat;
}

export type DashboardSegment =
  | "all-active-subscriptions"
  | "all-cancelled-subscriptions"
  | "first-workout"
  | "worked-out-today"
  | "worked-out-this-week"
  | "inactive-7d"
  | "trials-ending-48h"
  | "streak-at-risk";

export interface DashboardSegmentRow {
  id: number;
  name: string;
  email: string;
  subscriptionStatus: string;
  lastLoginAt: string | null;
  lastWorkoutAt: string | null;
}

// Matches the backend's real Pagination<T> (page/limit) — deliberately NOT
// userService.ts's PaginatedResponse<T>, which uses currentPage and has no
// limit field. The two are not interchangeable.
export interface DashboardSegmentPage {
  data: DashboardSegmentRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const emptySegmentPage = (
  page: number,
  limit: number
): DashboardSegmentPage => ({
  data: [],
  total: 0,
  page,
  limit,
  totalPages: 1,
});

// Safe-default on failure rather than rethrow: this page renders one overview
// widget plus 8 independent segment widgets, so one failed network call must
// not blank the whole page.
export const getOverview = async (): Promise<DashboardOverview | null> => {
  try {
    const response = await api.get<DashboardOverview>(
      "/admin/dashboard/overview",
      { timeout: 0 }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching dashboard overview:", error);
    return null;
  }
};

export const getSegment = async (
  segment: DashboardSegment,
  page: number = 1,
  limit: number = 20
): Promise<DashboardSegmentPage> => {
  try {
    const response = await api.get<DashboardSegmentPage>(
      `/admin/dashboard/segments/${segment}`,
      { params: { page, limit }, timeout: 0 }
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching dashboard segment "${segment}":`, error);
    return emptySegmentPage(page, limit);
  }
};
